/**
 * Browser fallback for the visitor's country, used when the CDN header the
 * backend reads (visitorCountry) is absent. Timezone first, since it follows
 * the device's location; the locale's region second, since "en-US" is often
 * just the browser default. Both are hints, not facts — the visitor can
 * always pick their country.
 */
const TIMEZONE_COUNTRY: Record<string, string> = {
  'Africa/Lagos': 'NG',
  'Africa/Accra': 'GH',
  'Africa/Nairobi': 'KE',
  'Africa/Johannesburg': 'ZA',
  'Africa/Kampala': 'UG',
  'Africa/Dar_es_Salaam': 'TZ',
  'Africa/Kigali': 'RW',
  'Africa/Douala': 'CM',
  'Africa/Dakar': 'SN',
  'Africa/Abidjan': 'CI',
  'Africa/Lusaka': 'ZM',
  'Africa/Cairo': 'EG',
  'Africa/Addis_Ababa': 'ET',
  'Africa/Kinshasa': 'CD',
  'Africa/Lubumbashi': 'CD',
  'Africa/Casablanca': 'MA',
  'Africa/Harare': 'ZW',
  'Africa/Gaborone': 'BW',
  'Africa/Blantyre': 'MW',
  'Africa/Maputo': 'MZ',
  'Africa/Windhoek': 'NA',
  'Africa/Freetown': 'SL',
  'Africa/Monrovia': 'LR',
  'Africa/Banjul': 'GM',
  'Africa/Porto-Novo': 'BJ',
  'Africa/Lome': 'TG',
  'Africa/Niamey': 'NE',
  'Africa/Bamako': 'ML',
  'Africa/Ouagadougou': 'BF',
  'Europe/London': 'GB',
  'Europe/Dublin': 'IE',
  'Asia/Kolkata': 'IN',
  'Asia/Calcutta': 'IN',
  'Asia/Dubai': 'AE',
  'Asia/Riyadh': 'SA',
  'America/Toronto': 'CA',
  'America/Vancouver': 'CA',
  'America/Edmonton': 'CA',
  'America/Winnipeg': 'CA',
  'America/Halifax': 'CA',
  'America/New_York': 'US',
  'America/Chicago': 'US',
  'America/Denver': 'US',
  'America/Phoenix': 'US',
  'America/Los_Angeles': 'US',
  'America/Anchorage': 'US',
  'Pacific/Honolulu': 'US',
  'Australia/Sydney': 'AU',
  'Australia/Melbourne': 'AU',
  'Australia/Perth': 'AU',
};

export interface BrowserCountryHints {
  timeZone?: string;
  languages?: readonly string[];
}

export function readBrowserHints(): BrowserCountryHints {
  let timeZone: string | undefined;
  try {
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    timeZone = undefined;
  }
  const languages = typeof navigator === 'undefined'
    ? []
    : (navigator.languages?.length ? navigator.languages : [navigator.language]).filter(Boolean);
  return { timeZone, languages };
}

/** ISO alpha-2 code from browser hints, or null when none points anywhere. */
export function countryFromBrowser(hints: BrowserCountryHints = readBrowserHints()): string | null {
  if (hints.timeZone && TIMEZONE_COUNTRY[hints.timeZone]) {
    return TIMEZONE_COUNTRY[hints.timeZone] ?? null;
  }
  for (const language of hints.languages ?? []) {
    const region = language.split(/[-_]/)[1];
    if (region && /^[A-Za-z]{2}$/.test(region)) return region.toUpperCase();
  }
  return null;
}
